import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface BetaFeatureAccess {
  featureId: string;
  featureName: string;
  hasAccess: boolean;
}

export function useBetaFeatureAccess(featureName: string) {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setHasAccess(false);
      setIsLoading(false);
      return;
    }

    checkAccess();
  }, [user, featureName]);

  const checkAccess = async () => {
    if (!user) return;

    try {
      // First get the feature ID by name
      const { data: feature, error: featureError } = await supabase
        .from('beta_features')
        .select('id, is_enabled')
        .eq('name', featureName)
        .single();

      if (featureError || !feature) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      // If the feature is not enabled globally, no one has access
      if (!feature.is_enabled) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      // Check if the user has access to this feature
      const { data: access, error: accessError } = await supabase
        .from('beta_feature_access')
        .select('id')
        .eq('feature_id', feature.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (accessError) {
        console.error('Error checking beta feature access:', accessError);
        setHasAccess(false);
      } else {
        setHasAccess(!!access);
      }
    } catch (error) {
      console.error('Error checking beta feature access:', error);
      setHasAccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return { hasAccess, isLoading };
}

// Hook to check access for multiple features at once
export function useBetaFeaturesAccess(featureNames: string[]) {
  const { user } = useAuth();
  const [accessMap, setAccessMap] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setAccessMap({});
      setIsLoading(false);
      return;
    }

    checkAccess();
  }, [user, featureNames.join(',')]);

  const checkAccess = async () => {
    if (!user) return;

    try {
      // Get all requested features
      const { data: features, error: featureError } = await supabase
        .from('beta_features')
        .select('id, name, is_enabled')
        .in('name', featureNames);

      if (featureError || !features) {
        setAccessMap({});
        setIsLoading(false);
        return;
      }

      // Get enabled feature IDs
      const enabledFeatures = features.filter(f => f.is_enabled);
      const enabledFeatureIds = enabledFeatures.map(f => f.id);

      if (enabledFeatureIds.length === 0) {
        setAccessMap({});
        setIsLoading(false);
        return;
      }

      // Check user's access to enabled features
      const { data: accessList, error: accessError } = await supabase
        .from('beta_feature_access')
        .select('feature_id')
        .eq('user_id', user.id)
        .in('feature_id', enabledFeatureIds);

      if (accessError) {
        console.error('Error checking beta feature access:', accessError);
        setAccessMap({});
      } else {
        const userFeatureIds = new Set(accessList?.map(a => a.feature_id) || []);
        const newAccessMap: Record<string, boolean> = {};
        
        for (const feature of enabledFeatures) {
          newAccessMap[feature.name] = userFeatureIds.has(feature.id);
        }
        
        setAccessMap(newAccessMap);
      }
    } catch (error) {
      console.error('Error checking beta features access:', error);
      setAccessMap({});
    } finally {
      setIsLoading(false);
    }
  };

  return { accessMap, isLoading };
}
